import { Injectable } from '@angular/core';

@Injectable()
export class LMProcessIndicatorService {
  public formatProcessIndicatorData(dataView: LMDataView, columnDef: ListGridColumn): LMProcessIndicatorData {
    const jsonString: string = <string>dataView.fields[columnDef.displayField.fieldId].value;

    if (jsonString === '') {
      return null;
    }

    return JSON.parse(jsonString);
  }

  public getTemplateData(processIndicatorData: LMProcessIndicatorData): Array<LMProcessIndicatorDisplayData> {
    let openIndex = 0;

    if (processIndicatorData === null) {
      return [];
    }

    const templateData: Array<LMProcessIndicatorDisplayData> = [];
    processIndicatorData.steps.forEach((step, index) => {
      // Track steps that are still open
      if (step.status === 'O') {
        step['openIndex'] = openIndex;
        openIndex++;
      }

      const data: LMProcessIndicatorDisplayData = {
        stepId: step.stepId,
        label: step.label,
        completionStatus: step.completionStatus,
        displayCompletionStatus: this.getCompletionStatusDisplayValue(step.completionStatus),
        displayStatus: step.completionMessage ? step.completionMessage : this.getStatusDisplayValue(step.status),
        status: step.status,
        elapsedTime: this.getElapsedTimeDisplayValue(step.startTime, step.completionTime),
        startTime: this.isValidTime(step.startTime) ? `${this.getTimeDisplayValue(step.startTime)}` : null,
        completedTime: this.isValidTime(step.completionTime)  ? `${this.getTimeDisplayValue(step.completionTime)}` : null,
        stepApprovers: step.stepApprovers,
        stepApproversDisplay: this.getStepApproversDisplayValues(step.stepApprovers),
        elapsedTimeToolTip: null,
        completionStatusInfo: this.getCompletionStatusInfo(step, processIndicatorData.steps),
        stepApproversOverflowContextMenuItems: null
      }

      if (data.stepApproversDisplay.length > 5) {
        data.stepApproversOverflowContextMenuItems = data.stepApproversDisplay.slice(5, data.stepApproversDisplay.length);
        data.stepApproversOverflowToolTipHTML = this.getStepApproverOverflowToolTipHTML(data.stepApproversOverflowContextMenuItems);

        // Remove overflow from array
        data.stepApproversDisplay.splice(5);
      }

      data.elapsedTimeToolTip = this.getElapsedTimeTooltip(data);

      templateData.push(data);
    });

    return templateData;
  }


  /*
    if ProcessIndicatorStep.status = I
      - display elapsed time since startTime ?

    if ProcessIndicatorStep.status = C AND
      - ProcessIndicatorStep.completionStatus = P, display solid circle
      - ProcessIndicatorStep.completionStatus = N, display red solid circle?
      - ProcessIndicatorStep.completionStatus = T, display clock?

    if ProcessIndicatorStep.status = X AND
      - ProcessIndicatorStep.completionStatus = N, display X over open grey circle
      - ProcessIndicatorStep.completionStatus = P/T/blank, display open grey circle
  */
  /**
   * COMPLETION STATUS
   * @param step
   */
  private getCompletionStatusInfo(step: LMProcessIndicatorStep, steps: Array<LMProcessIndicatorStep>): LMProcessIndicatorCompletionStatusInfo {
    let completionStatusInfo: LMProcessIndicatorCompletionStatusInfo = {
      indicatorClass: this.getIndicatorClasses(step, steps)
    };

    switch (step.status) {
      case 'C':
        completionStatusInfo = {
          ...completionStatusInfo,
          iconInfo: this.getCompletedStatus(step)
        }
        break;
      case 'X':
        completionStatusInfo = {
          ...completionStatusInfo,
          iconInfo: this.getInvalidStatus()
        }
        break;
      case 'E':
        completionStatusInfo = {
          ...completionStatusInfo,
          iconInfo: this.getErrorStatus()
        }
        break;
      case 'I':
      case 'O':
        completionStatusInfo = {
          ...completionStatusInfo,
          iconInfo: this.getInDefaultStatus(step)
        }
        break;
    }

    return completionStatusInfo;
  }

  /**
   * Return either the current step (step in progress)
   * Or return the first open step
   * @private
   */
  public getStepInProgress(templateData: Array<LMProcessIndicatorDisplayData>): LMProcessIndicatorDisplayData {
    const completedSteps = templateData.filter((s) => s.status === 'C' || s.status === 'I' || s.status === 'E');
    return completedSteps.length === 0 ? templateData[0] : completedSteps[completedSteps.length - 1];
  }

  /**
   * Used to change the color of the icon
   * @param icon
   * @param isToolTip (need to reverse color if tooltip, tooltip has black bg color)
   * @private
   */
  public getApproverIconColorClass(icon: LMProcessIndicatorIcon, isToolTip?: boolean): LMProcessIndicatorColorClassName {
    switch (icon) {
      case 'T': return 'lm-status-yellow';
      case 'N': return isToolTip ? 'lm-status-white' : 'lm-status-gray';
      case 'R':
      case 'E': return 'lm-status-red';
      case 'A': return 'lm-status-green';
      case 'U': return 'lm-status-purple';
    }
  }

  public getTagClasses(approver: LMProcessIndicatorDisplayStepApprover): string {
    const classes = ['tag'];

    if (approver.displayTag?.type === 'secondary') {
      classes.push('secondary');
    }

    if (approver.displayTag?.type === 'primary') {
      classes.push('primary');
    }

    return classes.join('');
  }

  /**
   * STATUS
   * @param step
   * @private
   */
  private getIndicatorClasses(step: LMProcessIndicatorStep, steps: Array<LMProcessIndicatorStep>): string {
    let cssClass: string;

    switch (step.status) {
      case 'I':
        cssClass = 'current processing';
        break
      case 'C':
      case 'X':
        cssClass = 'darker';
        break
      case 'O':
        cssClass = step['openIndex'] === 0 && steps.filter((s) => s.status === 'I').length === 0 ? 'current processing' : '';
        break;
      default:
        cssClass = '';
        break
    }

    return cssClass;
  }

  /**
   * COMPLETION STATUS ICONS AND CLASS IF "STATUS" IS "C" (completed)
   * @param step
   * @private
   */
  private getCompletedStatus(step: LMProcessIndicatorStep): LMProcessIndicatorCompletionStatusInfoIconInfo {
    switch (step.completionStatus) {
      case 'N': return { icon: 'rejected-solid', colorClass: 'lm-status-red'};
      case 'T': return { icon: 'pending-solid', colorClass: 'lm-status-yellow'};
      case 'U': return { icon: 'in-progress-solid', colorClass: 'lm-status-purple'};
      default: return { icon: null, colorClass: null};
    }
  }

  /**
   * COMPLETION STATUS ICONS AND CLASS IF "STATUS" IS "X" (invalid)
   * @param step
   * @private
   */
  private getInvalidStatus(): LMProcessIndicatorCompletionStatusInfoIconInfo {
    return { icon: 'rejected-solid', colorClass: 'lm-status-gray'};
  }

  /**
   * COMPLETION STATUS ICONS AND CLASS IF "STATUS" IS "E" (error)
   * @param step
   * @private
   */
  private getErrorStatus(): LMProcessIndicatorCompletionStatusInfoIconInfo {
    return { icon: 'error-solid', colorClass: 'lm-status-red'};
  }

  /**
   * COMPLETION STATUS ICONS AND CLASS IF "STATUS" IS "O or I" (open, inProgress)
   * @param step
   * @private
   */
  private getInDefaultStatus(step: LMProcessIndicatorStep): LMProcessIndicatorCompletionStatusInfoIconInfo {
    let iconInfo: LMProcessIndicatorCompletionStatusInfoIconInfo;

    switch (step.completionStatus) {
      case 'T':
        iconInfo = { icon: 'pending-solid', colorClass: 'lm-status-yellow'};
        break
      default:
        iconInfo = { icon: null, colorClass: null};
        break
    }

    return iconInfo;
  }

  private getStepApproverOverflowToolTipHTML(stepApprovers: Array<LMProcessIndicatorDisplayStepApprover>): string {
    const html = stepApprovers.map((approver) => `
      <div class="item">
        ${approver.icon ? `
        <svg class="icon ${this.getApproverIconColorClass(approver.icon)}" focusable="false" aria-hidden="true" role="presentation">
          <use href="#icon-${approver.displayIcon}"></use>
        </svg>` : ''}
        ${approver.approver}
        ${approver.tag ? `<span class="${this.getTagClasses(approver)}">${approver.displayTag.value}</span>` : ''}
      </div>`

      // Remove hyperlink for now, there is no way to click on it in a tooltip
      // ${approver.email ? `<a class="hyperlink" href="mailto:${approver.email}"><span>${approver.approver}</span></a>` : `${approver.approver}`}
    ).join('').trim();

    return html;
  }

  private getElapsedTimeTooltip(data: LMProcessIndicatorDisplayData): string {
    return `
            ${data.startTime ? `<div>Begin: ${data.startTime}</div>` : ''}
            ${data.completedTime ? `<div>End: ${data.completedTime}</div>` : ''}
        `
  }

  // TODO: Translation
  private getElapsedTimeDisplayValue(startTime: string, completionTime: string): string {
    const _startTime = startTime && Number(startTime) !== -1 ? this.getDate(startTime) : undefined;
    const _completionTime = completionTime && Number(completionTime) !== -1 ? this.getDate(completionTime) : undefined;
    const timeToComplete = _completionTime && _startTime ? _completionTime.getTime() - _startTime.getTime() : undefined;
    const timeElapsedSinceStart = _startTime ?  new Date().getTime() - _startTime.getTime() : 0;

    if (_startTime === undefined && _completionTime === undefined && timeToComplete === undefined) {
      return;
    }

    const ms = timeToComplete ? timeToComplete : timeElapsedSinceStart;
    const minutes = (ms / 1000)  / 60;

    if (minutes < 1) {
      return `Less than 1 minute`;
    }

    if (Math.round(minutes) === 1) {
      return `One minute`;
    }

    if (Math.round(minutes) === 60) {
      return `One hour`;
    }

    if (Math.round(minutes) > 60 && Math.round(minutes) < 1440) {
      return `${Math.round(minutes / 60)} hours`;
    }

    if (Math.round(minutes) === 1440) {
      return 'One day';
    }

    if (Math.round(minutes) > 1440 && Math.round(minutes) < 43800) {
      return `${Math.round(minutes / 1440)} days`
    }

    if (Math.round(minutes) === 43800) {
      return 'One Month';
    }

    if (Math.round(minutes) > 43800 && Math.round(minutes) < 525600) {
      return `${Math.round(minutes / 43800)} months`
    }

    if (Math.round(minutes) === 525600) {
      return 'One Year';
    }

    if (Math.round(minutes) > 525600) {
      return `${Math.round(minutes / 525600)} years`
    }

    return `${Math.round(minutes)} minutes`;
  }

  private isValidTime(time: string): boolean {
    if (time === '-1' || Number(time) === -1) {
      return;
    }

    if (time === undefined) {
      return;
    }

    return true;
  }

  private getTimeDisplayValue(time: string): string {
    return Soho.Locale.formatDate(this.getDate(time), {pattern: 'MMM dd, yyyy | hh:mm a'});
  }

  private getStepApproversDisplayValues(stepApprovers: Array<LMProcessIndicatorApprover>): Array<LMProcessIndicatorDisplayStepApprover> {
    const stepApproversMap = stepApprovers.map((approver) => Object.assign(approver, <LMProcessIndicatorDisplayStepApprover> {
      icon: approver.icon,
      displayIcon: this.getIconDisplayValue(approver.icon),
      displayTag: this.getTagDisplayValue(approver.tag),
      colorClass: this.getApproverIconColorClass(approver.icon)
    }));

    return stepApproversMap;
  }

  private getStatusDisplayValue(value: LMProcessIndicatorStatus): LMProcessIndicatorDisplayStatus {
    const status = {
      O: 'Open' as 'Open',
      I: 'In Progress' as 'In Progress',
      C: 'Completed' as 'Completed',
      X: 'Invalid' as 'Invalid'
    }

    return status[value];
  }

  private getCompletionStatusDisplayValue(value: LMProcessIndicatorCompletionStatus): LMProcessIndicatorCompletionDisplayStatus {
    const status = {
      T: 'Timeout' as 'Timeout',
      N: 'Negative' as 'Negative',
      P: 'Positive' as 'Positive',
      U: 'Route' as 'Route'
    }

    return status[value];
  }

  private getIconDisplayValue(icon: LMProcessIndicatorIcon): LMProcessIndicatorIcon {
    // Map icon names to infor-design-system icon names
    const icons = {
      T: 'pending' as 'T',
      R: 'rejected-outline' as 'R',
      A: 'success' as 'A',
      N: 'empty-circle' as 'N',
      E: 'error' as 'E',
      U: 'in-progress' as 'U'
    }

    return icons[icon];
  }

  private getTagDisplayValue(tag: string): LMProcessIndicatorDisplayTag {
    return { type: 'primary', value: tag }
  }

  private getDate(time: string): Date {
    if (time === undefined) {
      return;
    }

    const offset = new Date().getTimezoneOffset() / 60;
    const parts = time.toString().split('');
    const year = Number(parts.slice(0, 4).join(''));
    const month = Number(parts.slice(4, 6).join(''));
    const day = Number(parts.slice(6, 8).join(''));
    const HH = Number(parts.slice(8, 10).join('')) - offset;
    const mm = Number(parts.slice(10, 12).join(''));
    const s = Number(parts.slice(12, 14).join(''));
    const ms = Number(parts.slice(14, 16).join(''));

    return new Date(year, month - 1, day, HH, mm, s, ms)
  }
}
